import { LEVEL, LEVEL_NAMES } from "../utils/constants/levels"
import { calculateProgramExpirationDate } from "../utils/displayUtils"
import { useMembershipJourneyContext } from "../MembershipJourneyContext"

/**
 * Custom hook to calculate program and level deadlines with cross-curriculum fallback.
 *
 *
 * @returns {Object} Program deadline data
 * @returns {Array} levelsData - Array of level objects with calculated expiration dates
 * @returns {Object} associateProgram - Program-level data with expiration date
 * @returns {Object} level1Data - Level 1 specific data
 * @returns {Object} level3Data - Level 3 specific data
 * @returns {boolean} allLevelsComplete - Whether all levels are completed
 *
 * @example
 * const { levelsData, associateProgram } = useProgramDeadlines()
 * console.log(levelsData[2].expirationDate) // Level 3 deadline
 * console.log(associateProgram.expirationDate) // Program deadline
 */
export const useProgramDeadlines = () => {
  const { pgmProgress } = useMembershipJourneyContext()
  const levels = pgmProgress?.levels || []

  // Build all three levels from pgmProgress with fallback expiration calculation
  const levelKeys = [LEVEL.LEVEL_1, LEVEL.LEVEL_2, LEVEL.LEVEL_3]
  const levelsData = levelKeys.map(levelKey => {
    const levelData = levels.find(l => l.key === levelKey)

    if (levelData) {
      const isCompleted = levelData.status === "complete"
      const isStarted = levelData.status !== "locked"

      // Use backend expiration date if available, otherwise calculate from Level 1
      // This handles cross-curriculum transitions (e.g., A1 -> FO3)
      let expirationDate = levelData.expirationDate
      if (!expirationDate && !isCompleted && isStarted) {
        expirationDate = calculateProgramExpirationDate(
          levels,
          pgmProgress?.programType,
        )
      }

      return {
        levelKey,
        levelName: LEVEL_NAMES[levelKey],
        isCompleted,
        isStarted,
        startDate: levelData.startDate,
        completionDate: levelData.completionDate,
        expirationDate,
      }
    } else {
      return {
        levelKey,
        levelName: LEVEL_NAMES[levelKey],
        isCompleted: false,
        isStarted: false,
      }
    }
  })

  const level1Data = levelsData.find(p => p.levelKey === LEVEL.LEVEL_1)
  const level3Data = levelsData.find(p => p.levelKey === LEVEL.LEVEL_3)
  const allLevelsComplete = levelsData.every(level => level.isCompleted)

  // Calculate program expiration date with fallback for cross-curriculum users
  let programExpirationDate = pgmProgress?.programExpirationDate
  if (!programExpirationDate && !allLevelsComplete) {
    programExpirationDate = calculateProgramExpirationDate(
      levels,
      pgmProgress?.programType,
    )
  }

  const associateProgram = {
    isCompleted: allLevelsComplete,
    startDate: level1Data?.startDate,
    completedDate:
      allLevelsComplete && level3Data?.completionDate
        ? level3Data.completionDate
        : null,
    expirationDate: programExpirationDate,
  }

  return {
    levelsData,
    associateProgram,
    level1Data,
    level3Data,
    allLevelsComplete,
  }
}
